import React from 'react';

import {
  Button,
  IconButton,
  Tooltip,
  ButtonGroup,
  Stack,
  Box,
} from '@mui/material';
import BrowserUtil from '../../../../../util/BrowserUtil';
import RightsUserUtil from '../../../../../util/rights/RightsUserUtil';
import SortUtil, { SortDirections } from '../../../../../util/SortUtil';
import ConnectGroupDeviceAssign from '../../GroupDeviceAssign/GroupDeviceAssign';
import { getPolyglot } from '../../../../../i18n';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { Group } from '../../../../../redux/groups/api/group.model';
import { useAppSelector } from '../../../../../redux/store.model';
import { Device } from '../../../../../redux/commander/api/device.model';
import DeviceModel from '../../../../../redux/commander/api/DeviceModel';
import { STModal } from '../../../../commons/Modal';

import './GroupEditDevices.css';
import { AddIcon, DeleteIcon } from '../../../../../theme/icons';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { getRightUserUtilSelector } from '../../../../../redux/rights/selectors';
import { useDevices } from '../../../../../redux/commander/hooks/useDevices';
import { useShowMessage } from '../../../../../util/hooks';
import { GroupAPI } from '../../../../../redux/groups/api/GroupAPI';
import LoadingButton from '@mui/lab/LoadingButton';
import { getMyWorkspaceIdSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';

const browserUtil = new BrowserUtil();
const getRowId = (row: Device) => row.uuid;
interface OwnProps {
  group?: Group;
}

function loadButtonDisplayPreferences(
  rightsUserUtil: RightsUserUtil,
  group?: Group,
  workspaceId?: string
) {
  const showRemoveGroupDeviceBotton =
    rightsUserUtil.hasRightsToRemoveGroupDevice(group?.id, workspaceId);
  const showAssignGroupDeviceBotton =
    rightsUserUtil.hasRightsToAssignGroupDevice(group?.id, workspaceId);
  return { showRemoveGroupDeviceBotton, showAssignGroupDeviceBotton };
}

const GroupEditDevicesFunctional = (props: OwnProps) => {
  const { group } = props;
  const polyglot = getPolyglot();
  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const workspaceId = useAppSelector(getMyWorkspaceIdSelector);
  const { showRemoveGroupDeviceBotton, showAssignGroupDeviceBotton } =
    loadButtonDisplayPreferences(rightsUserUtil, group, workspaceId);
  const { data: devices } = useDevices(group?.devices || []);
  const handlingErrorsApi = useShowErrorMessage();
  const showMessage = useShowMessage();
  const [showMessageDeleteGroupDevice, setShowMessageDeleteGroupDevice] =
    React.useState(false);
  const [showGroupDeviceAssign, setShowGroupDeviceAssign] =
    React.useState(false);
  const [deleteDeviceId, setDeleteDeviceId] = React.useState<null | string>(
    null
  );

  const devicesSorted = React.useMemo(() => {
    if (devices && devices.length > 0) {
      return SortUtil.multisort(devices, ['name'], [SortDirections.ASC]);
    }
    return devices;
  }, [devices]);

  const [deleteDeviceGroup, { isLoading: isLoadingDeleteDeviceGroup }] =
    GroupAPI.useDeleteDeviceGroupMutation();
  const deleteDevice = async (event: unknown, deviceId: string) => {
    try {
      if (!group) throw new Error('Group not found');
      await deleteDeviceGroup({
        groupId: group.id,
        commanderId: deviceId,
      }).unwrap();
      showMessage(polyglot.t('group.remove_group_device_successful_message'));
      closeMessageDeleteGroupDevice();
    } catch (error) {
      handlingErrorsApi(error);
    }
  };

  const openMessageDeleteGroupDevice = (event: unknown, deviceId: string) => {
    setShowMessageDeleteGroupDevice(true);
    setDeleteDeviceId(deviceId);
  };

  const closeMessageDeleteGroupDevice = () => {
    setShowMessageDeleteGroupDevice(false);
    setDeleteDeviceId(null);
  };
  const openGroupDeviceAssign = () => {
    setShowGroupDeviceAssign(true);
  };
  const closeGroupDeviceAssign = () => {
    setShowGroupDeviceAssign(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'avatar',
      headerName: '',
      sortable: false,
      flex: 0.5,
      renderCell: (params) => (
        <img
          src={DeviceModel.getDeviceIcon(params.row.iconURL)}
          className="mini"
          alt="device-icon"
          height={!browserUtil.getIsPhone() ? '30%' : '50%'}
          width={!browserUtil.getIsPhone() ? '30%' : '50%'}
        />
      ),
    },
    {
      field: 'name',
      headerName: polyglot.t('group.users.name'),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        const device = params.row as Device;
        return (
          <ButtonGroup>
            {/* Button delete */}
            {showRemoveGroupDeviceBotton && (
              <Tooltip title={polyglot.t('group.tooltip.remove_group_device')}>
                <IconButton
                  size="small"
                  onClick={(event) =>
                    openMessageDeleteGroupDevice(event, device.uuid)
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </ButtonGroup>
        );
      },
    },
  ];

  return (
    <>
      <ConnectGroupDeviceAssign
        groupId={group?.id}
        showGroupDeviceAssign={showGroupDeviceAssign}
        closeGroupDeviceAssign={closeGroupDeviceAssign}
      />

      <Stack spacing={1}>
        <div style={{ height: '100%', width: '100%' }}>
          <DataGrid
            rows={devicesSorted}
            columns={columns}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            getRowId={getRowId}
          />
        </div>
        <Box>
          {showAssignGroupDeviceBotton && (
            <Tooltip
              title={polyglot.t('group.tooltip.open_window_assign_device')}
            >
              <IconButton
                color="primary"
                size="large"
                sx={{ float: 'right' }}
                onClick={openGroupDeviceAssign}
                id="ButtonAssignGroupDevice"
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>

      {/* Modal window called when the user wants to delete device*/}
      <STModal
        open={showMessageDeleteGroupDevice}
        onClose={closeMessageDeleteGroupDevice}
        buttonActions={
          <>
            <Button
              variant="text"
              onClick={closeMessageDeleteGroupDevice}
              disabled={isLoadingDeleteDeviceGroup}
            >
              {polyglot.t('general.no')}
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              onClick={(event) => deleteDevice(event, deleteDeviceId as string)}
              loading={isLoadingDeleteDeviceGroup}
            >
              {polyglot.t('general.yes')}
            </LoadingButton>
          </>
        }
      >
        <p>{polyglot.t('group.remove_group_device_message')}</p>
      </STModal>
    </>
  );
};

export default GroupEditDevicesFunctional;
